import { __awaiter } from "tslib";
import React, { useState, useEffect } from 'react';
import { default as getLabelFromStorage } from "@pb/utils/labels/getLabelFromStorage";
import { imageTypes } from "@pb/utils/image/getImageUrl";
import { default as useRequest } from "@pb/utils/reactHooks/useRequest";
import { default as getSettingsObsolete } from "@pb/utils/common/getSettings";
import { getLocale } from "@pb/utils/locale";
import { isOptimizelyVanillaFeatureEnabled } from "@pb/utils/optimizely";
import classNames from 'classnames';
import Markup from "../../helpers/Markup";
import Label from "../Label";
import { Accordion, AccordionItem } from "../../ui/Accordion";
import { Button, CTALink } from "../../ui/CTA";
import { MeasureIcon, InfoIcon } from "../../ui/Icon";
import Image from "../../ui/Image";
import Modal from "../../ui/Modal";
import StatusMessage from "../../ui/StatusMessage";
import TabToggle from "../../ui/TabToggle";
import BodyText from "../../ui/Typography/texts/BodyText";
import HeadingSmall from "../../ui/Typography/titles/HeadingSmall";
import SizeRangeTabContent from './components/SizeRangeTabContent';
import getFormattedBrandName from './helpers/getFormattedBrandName';
import { trackOnView, trackOnClickMeasurement, trackOnSizeGuideClose, trackOnSizeGuideType, } from './helpers/tracking';
import styles from './SizeGuideModal.module.scss';
const SizeGuideModal = ({ sizeGuideLabels = {
    sizeGuide: 'Size guide',
    findMySize: 'Find my size',
    howToMeasure: 'How to measure',
    selectSizeRange: 'Size range',
    sizeGuideLinkText: 'Size guide link text',
    noSizeGuideTitle: 'Oh, hello there!',
    noSizeGuideDescription: 'For this product we dont have a size guide available at this moment in time.',
    noSizeGuideClose: 'Close',
}, data = {}, toggleIconType = 'chevron', articleData = {}, handleSizeGuideModal, sizeGuideComponent, isCTALink, isBossPDP, }) => {
    const [shouldShowModal, setShouldShowModal] = useState(false);
    const [sizeGuideData, setSizeGuideData] = useState({});
    const [isSizeGuideApiCalled, setIsSizeGuideApiCalled] = useState(false);
    const [sizeGuideTableData, setSizeGuideTableData] = useState([]);
    const [sizeGuideHeadline, setSizeGuideHeadline] = useState(null);
    const [hasTabChanged, setHasTabChanged] = useState(false);
    const [onAccordionChange, setOnAccordionChange] = useState(false);
    const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
    const [isPXASizeGuideEnabled, setIsPXASizeGuideEnabled] = useState(false);
    const responsiveSizeGuideEnabled = process.env.IS_RESPONSIVE_SIZE_GUIDE_ENABLED === 'enabled';
    const currentLocale = getLocale();
    const addSizeGuideTableData = (sizeGuideLabel = '', sizeGuideTable = []) => {
        if (sizeGuideTable.length > 0) {
            setSizeGuideTableData((prevArray) => [
                ...prevArray,
                {
                    sizeGuideLabel,
                    sizeGuideTable,
                },
            ]);
        }
    };
    const populateSizeGuideTableData = (inData) => {
        setSizeGuideTableData([]);
        if (inData && Object.keys(inData).length) {
            addSizeGuideTableData(inData.sizeGuideOneLabel, inData.sizeGuideOne);
            addSizeGuideTableData(inData.sizeGuideTwoLabel, inData.sizeGuideTwo);
            addSizeGuideTableData(inData.sizeGuideThreeLabel, inData.sizeGuideThree);
        }
    };
    const getSizeGuideUrl = () => {
        const { department, code } = articleData;
        const brand = getFormattedBrandName(articleData === null || articleData === void 0 ? void 0 : articleData.brandName);
        const prefix = 'customer-service/sizeguide';
        const suffix = 'sizeguide.hm.sizeguide.json';
        const queryParams = `articleCode=${code}`;
        return `${prefix}/${department}/${brand}-${suffix}?${queryParams}`;
    };
    const getPXASizeGuideUrl = () => {
        const { code } = articleData;
        const articleCode = `${code}`.slice(0, 10);
        const domain = process.env.SIZE_GUIDE_PXA_API_BASE_URL;
        const prefix = 'pxa/v1';
        const suffix = 'sizeguide';
        const path = `${domain}/${prefix}/${currentLocale}/hm/${suffix}/${articleCode}`;
        return path;
    };
    useEffect(() => {
        var _a;
        (_a = isOptimizelyVanillaFeatureEnabled('pxfit_new_size_guide')) === null || _a === void 0 ? void 0 : _a.then((value) => {
            setIsPXASizeGuideEnabled(value);
        });
    }, []);
    const sizeGuideUrl = isPXASizeGuideEnabled ? getPXASizeGuideUrl() : getSizeGuideUrl();
    const { data: sizeGuideResults, execute: fetchSizeGuideData, isRequestMade, error, } = useRequest(sizeGuideUrl, {
        use: { apiVersion: false },
        manual: true,
    });
    const getOldSizeGuideUrl = () => {
        return `productpage/_jcr_content/product.sizeguide.${articleData === null || articleData === void 0 ? void 0 : articleData.code}.html`;
    };
    const { data: oldSizeGuideResults, execute: getOldSizeGuideData, isRequestMade: oldSizeGuideRequestMade, error: oldSizeGuideError, } = useRequest(getOldSizeGuideUrl(), {
        use: { apiVersion: false },
        manual: true,
    });
    const fetchOldSizeGuideData = () => {
        return ((articleData.brandName === undefined || articleData.brandName === 'H&M') &&
            data &&
            Object.keys(data).length === 0 &&
            getOldSizeGuideData());
    };
    useEffect(() => {
        const fetchData = () => __awaiter(void 0, void 0, void 0, function* () {
            const settings = yield getSettingsObsolete('nativeSizeGuideEnabled', process.env.SETTINGS_ENDPOINT);
            if (responsiveSizeGuideEnabled &&
                settings &&
                articleData &&
                Object.keys(articleData).length) {
                fetchSizeGuideData();
            }
            else {
                fetchOldSizeGuideData();
            }
        });
        if (isSizeGuideApiCalled)
            fetchData();
    }, [isSizeGuideApiCalled, articleData.code]);
    useEffect(() => {
        if (isSizeGuideApiCalled && (sizeGuideResults === null || sizeGuideResults === void 0 ? void 0 : sizeGuideResults.length) === 0) {
            fetchOldSizeGuideData();
        }
    }, [isSizeGuideApiCalled, sizeGuideResults]);
    useEffect(() => {
        if ((sizeGuideResults === null || sizeGuideResults === void 0 ? void 0 : sizeGuideResults.length) > 0) {
            populateSizeGuideTableData(sizeGuideResults[0]);
            setSizeGuideData(sizeGuideResults[0]);
        }
        else if (oldSizeGuideResults && Object.keys(oldSizeGuideResults).length > 0) {
            setSizeGuideData(oldSizeGuideResults);
            populateSizeGuideTableData(oldSizeGuideResults);
        }
        else {
            setSizeGuideData(data);
        }
    }, [sizeGuideResults, oldSizeGuideResults]);
    const getSizeRangeTabContents = (tableData, index) => (React.createElement(SizeRangeTabContent, { key: `SizeRangeTabContent-${index}`, tableData: tableData, isBossPDP: isBossPDP }));
    const getSizeRangeTabs = () => {
        const sizeRangeTabs = [];
        if (sizeGuideTableData.length > 1) {
            sizeGuideTableData.forEach((sizeGuideTable, index) => {
                sizeRangeTabs.push({
                    id: `sizeRangeTab${index}`,
                    heading: sizeGuideTable.sizeGuideLabel,
                    children: getSizeRangeTabContents(sizeGuideTable.sizeGuideTable, index),
                });
            });
        }
        else if (sizeGuideTableData.length === 1) {
            return React.createElement(SizeRangeTabContent, { tableData: sizeGuideTableData[0].sizeGuideTable });
        }
        return (React.createElement(TabToggle, { rememberTabChildrenState: true, className: styles.sizeRangeTabs, tabs: sizeRangeTabs, setHasTabChanged: setHasTabChanged }));
    };
    const getHowToMeasureItemHeading = (title) => {
        const isHeadingTitleValid = ((title === null || title === void 0 ? void 0 : title.split('-').length) || 0) > 0 &&
            !(typeof (title === null || title === void 0 ? void 0 : title.split('-').pop()) === 'undefined') &&
            title.split('-').pop().length > 0;
        if (title && isHeadingTitleValid) {
            return (React.createElement(HeadingSmall, { Tag: "h3", className: styles.howToMeasureItemHeading }, title.split('-').pop().trim()));
        }
        return null;
    };
    const sizeGuideSectionClasses = classNames({
        'size-guide-content': true,
        'scope-panel': true,
    });
    const showModal = () => {
        setShouldShowModal(true);
        setIsSizeGuideApiCalled(true);
    };
    const trackOnViewSizeGuide = () => {
        const { code, articleName } = articleData;
        trackOnView(sizeGuideResults, oldSizeGuideResults, code, articleName, isBossPDP);
    };
    useEffect(() => {
        if (shouldShowModal &&
            articleData &&
            (typeof sizeGuideResults !== 'undefined' || typeof oldSizeGuideResults !== 'undefined')) {
            trackOnViewSizeGuide();
        }
    }, [shouldShowModal, sizeGuideResults, oldSizeGuideResults, articleData, isBossPDP]);
    useEffect(() => {
        if (shouldShowModal &&
            onAccordionChange &&
            (typeof sizeGuideResults !== 'undefined' || typeof oldSizeGuideResults !== 'undefined')) {
            trackOnClickMeasurement(isAccordionExpanded, isBossPDP);
        }
    }, [isAccordionExpanded, onAccordionChange, shouldShowModal]);
    useEffect(() => {
        if (hasTabChanged) {
            trackOnSizeGuideType(isBossPDP);
            setHasTabChanged(false);
        }
    }, [isBossPDP, hasTabChanged]);
    const toggleSizeGuideModal = () => {
        setShouldShowModal(false);
    };
    const getModalContents = (dataContents) => {
        var _a, _b, _c;
        return (React.createElement("article", { className: styles.sizeGuideContainer },
            (dataContents === null || dataContents === void 0 ? void 0 : dataContents.bannerText) && (React.createElement(StatusMessage, { iconPosition: "start", type: "info", icon: "info", message: dataContents.bannerText })),
            (dataContents === null || dataContents === void 0 ? void 0 : dataContents.imgSrc) &&
                (!Array.isArray(dataContents === null || dataContents === void 0 ? void 0 : dataContents.introComponentItems) ||
                    ((_a = dataContents === null || dataContents === void 0 ? void 0 : dataContents.introComponentItems) === null || _a === void 0 ? void 0 : _a.length) === 0) && (React.createElement(Image, { url: dataContents === null || dataContents === void 0 ? void 0 : dataContents.imgSrc, altText: dataContents === null || dataContents === void 0 ? void 0 : dataContents.sizeGuideType, imageType: imageTypes.DEFAULT_IMAGE, shouldLazyLoad: false, className: styles.measureImage })),
            ((_b = dataContents === null || dataContents === void 0 ? void 0 : dataContents.introComponentItems) === null || _b === void 0 ? void 0 : _b.length) > 0 && (React.createElement(Accordion, { shouldAnimate: true, accordionType: "definition", toggleIconType: toggleIconType, shouldHighlightOpenToggleIcon: false, shouldCollapseSibling: true, className: styles.accordionContainer, setIsAccordionExpanded: setIsAccordionExpanded, onChange: () => setOnAccordionChange(!onAccordionChange) },
                React.createElement(AccordionItem, { id: "AccordionItem", headerLeftContent: sizeGuideLabels.howToMeasure },
                    (dataContents === null || dataContents === void 0 ? void 0 : dataContents.imgSrc) && (React.createElement(Image, { url: dataContents === null || dataContents === void 0 ? void 0 : dataContents.imgSrc, altText: dataContents === null || dataContents === void 0 ? void 0 : dataContents.sizeGuideType, imageType: imageTypes.DEFAULT_IMAGE, shouldLazyLoad: false, className: styles.measureImage })),
                    (dataContents === null || dataContents === void 0 ? void 0 : dataContents.introInformationText) && (React.createElement("div", { className: styles.introInformationText },
                        React.createElement(InfoIcon, { className: styles.infoIcon }),
                        dataContents.introInformationText)),
                    React.createElement("ul", { className: styles.howToMeasureItems }, (_c = dataContents === null || dataContents === void 0 ? void 0 : dataContents.introComponentItems) === null || _c === void 0 ? void 0 : _c.map((listItem, index) => (React.createElement(BodyText, { Tag: "li", key: listItem.description, className: styles.howToMeasureItem },
                        listItem.title && listItem.title.length > 0 && (React.createElement("div", { className: styles.howToMeasureItemNumber }, listItem.title ? index + 1 : '')),
                        React.createElement("div", { className: styles.howToMeasureItemTextSection },
                            getHowToMeasureItemHeading(listItem.title),
                            React.createElement(BodyText, { Tag: "p", className: styles.howToMeasureItemText }, listItem.description))))))))),
            sizeGuideTableData.length > 0 && (React.createElement("section", { className: styles.sizeRangeContainer },
                React.createElement(BodyText, { Tag: "h3", className: styles.sizeRangeHeading }, sizeGuideLabels.selectSizeRange),
                getSizeRangeTabs()))));
    };
    const getModalHeadline = () => {
        var _a, _b, _c;
        const headline = sizeGuideResults && ((_a = sizeGuideResults[0]) === null || _a === void 0 ? void 0 : _a.headline)
            ? sizeGuideResults[0].headline
            : oldSizeGuideResults && ((_b = oldSizeGuideResults[0]) === null || _b === void 0 ? void 0 : _b.headline)
                ? (_c = oldSizeGuideResults[0]) === null || _c === void 0 ? void 0 : _c.headline
                : sizeGuideLabels.sizeGuide;
        setSizeGuideHeadline(headline);
    };
    useEffect(() => {
        getModalHeadline();
    }, [sizeGuideResults, oldSizeGuideResults]);
    return (React.createElement(React.Fragment, null,
        isCTALink ? (React.createElement(CTALink, { iconElement: React.createElement(MeasureIcon, { size: "small" }), inline: true, Tag: "button", onClick: handleSizeGuideModal ? handleSizeGuideModal.handleSizeGuideClick : showModal },
            React.createElement(Label, { copyKey: sizeGuideLabels.sizeGuide }))) : (React.createElement(Button, { onClick: handleSizeGuideModal ? handleSizeGuideModal.handleSizeGuideClick : showModal, iconElement: React.createElement(MeasureIcon, { size: "small" }), size: "small", className: styles.sizeGuideButton, inverted: true },
            React.createElement(Label, { copyKey: sizeGuideLabels.sizeGuide }))),
        React.createElement(Modal, { closeButtonText: getLabelFromStorage('general.close'), isOpen: handleSizeGuideModal ? handleSizeGuideModal.isOpen : shouldShowModal, onClosing: () => trackOnSizeGuideClose(isBossPDP), toggleModal: handleSizeGuideModal ? handleSizeGuideModal.toggleSizeGuide : toggleSizeGuideModal, isImportedText: true, modalType: "stickyRight", className: styles.modalContainer, title: sizeGuideHeadline, disableRemoveScroll: true }, sizeGuideComponent ||
            (oldSizeGuideResults ? (React.createElement(Markup, { className: styles.sizeGuide, Tag: "div", sanitized: false, input: oldSizeGuideResults })) : (React.createElement("div", { className: sizeGuideSectionClasses }, (isRequestMade &&
                sizeGuideData &&
                Object.keys(sizeGuideData).length === 0 &&
                error) ||
                (oldSizeGuideRequestMade && oldSizeGuideError) ? (React.createElement("section", { className: styles.noSizeGuideContainer },
                React.createElement(MeasureIcon, { size: "xlarge", className: styles.measureIcon }),
                React.createElement(BodyText, { Tag: "h3", className: styles.noSizeGuideTitle }, sizeGuideLabels.noSizeGuideTitle),
                React.createElement(BodyText, { className: styles.noSizeGuideDescription }, sizeGuideLabels.noSizeGuideDescription),
                React.createElement(Button, { fullWidth: true, onClick: toggleSizeGuideModal }, sizeGuideLabels.noSizeGuideClose))) : (getModalContents(sizeGuideData))))))));
};
export default SizeGuideModal;
//# sourceMappingURL=SizeGuideModal.js.map